/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Product Offerings Step Confirmation (final step)
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.10.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Tooltip, Typography } from '@nexign/scc-ui-kit';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { connect, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { getTimeZone } from '@/main/components/Table/helpers/getTimeZone';
import { DEBOUNCE_DEFAULT_DELAY } from '@/main/modules/app/appConstants';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { icons } from '@assets/index';
import { LinkBlock, TextBlockModal, TextBlockStatus } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { ETextBlockStatus } from '@components/common/TextBlockStatus/textBlockStatusTypes';
import type { ISimpleTableColumns, ISimpleTableData } from '@components/SimpleTable/SimpleTable';
import getActionConflictsParsed from '@components/Table/helpers/getActionConflictsParsed';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardProductOfferings,
} from '@modules/internationalization/intSelectors';
import type { IWizardProductOfferingsTranslation } from '@modules/internationalization/types';
import type ICommonTranslation from '@modules/internationalization/types/common';
import { makeSelectGuardComponents } from '@modules/role';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { WizardStepConfirmationRemovedItemStyled } from '@modules/wizard/components/styled/WizardConfirmationStyled';
import WizardConfirmationStep from '@modules/wizard/components/WizardConfirmationStep';
import type { IWizardSummaryParam } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportStorageSaveAction } from '@modules/wizard/wizardActions';
import { getPageAccountInfoUrl, isPageAccountInfoUrlExternal } from '@pages/PageAccountInfo';
import { makeSelectFeatureEnabled, makeSelectIsFeatureWithVersionEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { getItemsWithCustomCollectiveProductConflicts } from '@pages/PageCollectiveProduct/utils/getCustomCollectiveProductConflicts';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import type { IRootReducer } from '@reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';

import { EProductsPageFrom } from '../../PageProducts/tariffsAndServicesConstants';
import { makeSelectProductsPageFrom } from '../../PageProducts/tariffsAndServicesSelectors';
import {
    productOfferingsGetSubscribersCheckListAction,
    productOfferingsSaveStorageAction,
    productOfferingsSetParamsAction,
    productOfferingsStartValidateAction,
} from '../wizardProductOfferingsActions';
import {
    EProductSubscribersActivationListField,
    EWizardProductOfferingsSteps,
} from '../wizardProductOfferingsConstants';
import {
    makeSelectProductOfferingsDateFrom,
    makeSelectProductOfferingsDateTo,
    makeSelectProductOfferingsIsDisconnect,
    makeSelectProductOfferingsListToConnect,
    makeSelectProductOfferingsProductOfferings,
    makeSelectProductOfferingsRemovedSubscribers,
    makeSelectProductOfferingsTimeFrom,
    makeSelectProductOfferingsTimeTo,
    makeSelectProductOfferingsTotalMegaPowers,
} from '../wizardProductOfferingsSelectors';
import { EActionConflictTypes } from '../wizardProductOfferingsTypes';
import type {
    IMegaPowersTotal,
    IProductOfferingsListItem,
    IProductOfferingsSetParamsActionPayload,
    IProductOfferingsSubscriberActivationItem,
} from '../wizardProductOfferingsTypes';
import { ProductOfferingsStepConfirmationTooltipText } from './styled/ProductOfferingsStepConfirmationStyled';

const ProductOfferingsStepConfirmation = (props: IConnectedProps & IDispatchProps) => {
    const {
        componentsFeature,
        dateFrom,
        dateTo,
        timeFrom,
        timeTo,
        isRoamingProductsFeatureEnabled,
        pageFrom,
        getSubscribersCheckList,
        isDisconnect,
        listToConnect,
        productOfferingsList,
        megaPowersList,
        productStorageSave,
        removedSubscribers,
        setParams,
        startValidate,
        storageSave,
        translationCommon,
        translationProductOfferings,
    } = props;
    const stepTranslations = translationProductOfferings[EWizardProductOfferingsSteps.StepConfirmation];

    // чтобы можно было работать отдельно со временем
    dayjs.extend(customParseFormat);

    const [tableData, setTableData] = useState<ISimpleTableData[]>([]);
    const [subsCount, setSubsCount] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    const [itemsWithCustomConflicts, setItemsWithCustomConflicts] = useState<string[]>([]);

    const isMegaPowersFeatureGreaterOrEqualThan2 = useSelector(
        makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
    );

    const timezoneOffset = getTimeZone(new Date().toISOString());

    const timeFromHHmm =
        dayjs(timeFrom, EFormatData.FullTime).isValid()
        ? `${dayjs(timeFrom, EFormatData.FullTime).format(EFormatData.OnlyHourMinutes)}${timezoneOffset}`
        : '';

    const timeToHHmm =
        dayjs(timeTo, EFormatData.FullTime).isValid()
        ? `${dayjs(timeTo, EFormatData.FullTime).format(EFormatData.OnlyHourMinutes)}${timezoneOffset}`
        : '';

    const summaryParams: IWizardSummaryParam[] = useMemo(() => {
        const result: IWizardSummaryParam[] = [];

        if (productOfferingsList.length) {
            result.push({
                title: !isDisconnect
                    ? translationProductOfferings.wizardSummary.activationServices
                    : translationProductOfferings.wizardSummary.deactivationServices,
                values: productOfferingsList.map((item) => item.name),
                limit: 4,
                limitMore: translationProductOfferings.wizardSummary.showMore,
                limitLess: translationProductOfferings.wizardSummary.showLess,
            });
        }

        if (
            isMegaPowersFeatureGreaterOrEqualThan2 &&
            Array.isArray(megaPowersList?.toActivate) &&
            megaPowersList.toActivate.length
        ) {
            result.push({
                title: translationProductOfferings.wizardSummary.megaPowersToActivate,
                values: [megaPowersList.toActivate.map((megaPower) => megaPower.name).join(', ')],
            });
        }

        if (
            isMegaPowersFeatureGreaterOrEqualThan2 &&
            Array.isArray(megaPowersList?.toDeactivate) &&
            megaPowersList.toDeactivate.length
        ) {
            result.push({
                title: translationProductOfferings.wizardSummary.megaPowersToDeactivate,
                values: [megaPowersList.toDeactivate.map((megaPower) => megaPower.name).join(', ')],
            });
        }

        result.push(
            {
                title: translationProductOfferings.wizardSummary.subscribersAmount,
                values: [`${subsCount}`],
            },
            {
                hide: isDisconnect,
                title: translationProductOfferings.wizardSummary.totalCost,
                values: [`${totalPrice} ${translationCommon.currency}`],
            },
        );

        if (pageFrom !== EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
            result.push({
                hide: isDisconnect,
                title: translationProductOfferings.wizardSummary.dateFrom,
                values: isRoamingProductsFeatureEnabled
                    ? [`${moment(dateFrom).format(EFormatData.FullDataShortYear)} ${timeFromHHmm}`]
                    : [moment(dateFrom).format(EFormatData.FullDataShortYear)],
            });
        }

        if (pageFrom !== EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
            result.push({
                hide: !dateTo,
                title: translationProductOfferings.wizardSummary.dateTo,
                values: isRoamingProductsFeatureEnabled
                    ? [`${moment(dateTo).format(EFormatData.FullDataShortYear)} ${timeToHHmm}`]
                    : [moment(dateTo).format(EFormatData.FullDataShortYear)],
            });
        }

        return result;
        // we don't need tranlations
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dateFrom,
        dateTo,
        isDisconnect,
        isMegaPowersFeatureGreaterOrEqualThan2,
        megaPowersList,
        productOfferingsList,
        subsCount,
        totalPrice,
    ]);

    const isCostWarn = useCallback((item) => {
        return item.cost > (item.balance || 0) + (item.creditLimit || 0);
    }, []);

    const removeHandler = useCallback(
        (id: string) => {
            setParams({
                removedSubscribers: removedSubscribers.concat(id),
            });
            productStorageSave();
        },
        [setParams, productStorageSave, removedSubscribers],
    );

    const recoverHandler = useCallback(
        (id: string) => {
            setParams({
                removedSubscribers: [...removedSubscribers.filter((item) => item !== id)],
            });
            productStorageSave();
        },
        [setParams, productStorageSave, removedSubscribers],
    );

    const getItemInfo = useCallback(
        (item: IProductOfferingsSubscriberActivationItem) => {
            if (isCostWarn(item) || item.isMegaPowersAvailable) {
                return (
                    <Tooltip
                        arrow
                        body={
                            <ProductOfferingsStepConfirmationTooltipText>
                                {isCostWarn(item) && (
                                    <Typography>
                                        {
                                            translationProductOfferings.ActivateOrderFinalStep.additionalWarnings
                                                .costWarning
                                        }
                                    </Typography>
                                )}

                                {item.isMegaPowersAvailable && (
                                    <Typography>
                                        {
                                            translationProductOfferings.ActivateOrderFinalStep.additionalWarnings
                                                .megaPowersCanBeChanged
                                        }
                                    </Typography>
                                )}
                            </ProductOfferingsStepConfirmationTooltipText>
                        }
                        disablePortal={false}
                        maxWidth={350}
                    >
                        <ImgStyled
                            className="small"
                            src={item.isMegaPowersAvailable ? icons.infoErrorSmallIcon : icons.infoBlueIcon}
                            width="24px"
                        />
                    </Tooltip>
                );
            } else {
                return '';
            }
        },
        [
            isCostWarn,
            translationProductOfferings.ActivateOrderFinalStep.additionalWarnings.costWarning,
            translationProductOfferings.ActivateOrderFinalStep.additionalWarnings.megaPowersCanBeChanged,
        ],
    );

    const getItemStatus = useCallback(
        (item: IProductOfferingsSubscriberActivationItem) => {
            const actionConflicts = item.actionConflicts || [];
            const actionConflictsParsed = getActionConflictsParsed<ETextBlockStatus>(
                actionConflicts,
                {
                    [EActionConflictTypes.ERROR]: translationCommon.corporateTable.actionConflictsTitle.error,
                    [EActionConflictTypes.WARNING]: translationCommon.corporateTable.actionConflictsTitle.warning,
                    tCustomErrors: translationCommon.corporateTable.actionConflictsTitle.numberCodeCustomErrors,
                },
                pageFrom,
            );

            return (
                <TextBlockModal
                    disabled={
                        actionConflictsParsed.status === ETextBlockStatus.OK ||
                        actionConflictsParsed.status === ETextBlockStatus.CHANGE
                    }
                    text={actionConflictsParsed.text}
                >
                    <TextBlockStatus status={actionConflictsParsed.status}>
                        <LinkBlock
                            disabled={
                                actionConflictsParsed.status === ETextBlockStatus.OK ||
                                actionConflictsParsed.status === ETextBlockStatus.CHANGE
                            }
                        >
                            {isDisconnect
                                ? stepTranslations.deactivateStatus[actionConflictsParsed.status]
                                : stepTranslations.activateStatus[actionConflictsParsed.status]}
                        </LinkBlock>
                    </TextBlockStatus>
                </TextBlockModal>
            );
        },
        [isDisconnect, translationCommon, stepTranslations, pageFrom],
    );

    /**
     * get ids of items with custom collective product conflicts
     */
    useEffect(() => {
        const itemsWithCollectiveProductConflicts = getItemsWithCustomCollectiveProductConflicts(listToConnect).map(
            (el) => el.id,
        );
        setItemsWithCustomConflicts(itemsWithCollectiveProductConflicts);
        const removedSet = new Set([...removedSubscribers, ...itemsWithCollectiveProductConflicts]);
        setParams({
            removedSubscribers: Array.from(removedSet),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listToConnect, setParams]);

    /**
     * Load table
     */
    useEffect(() => {
        getSubscribersCheckList();
    }, [getSubscribersCheckList]);

    /**
     * Preparing table data
     */
    useEffect(() => {
        setTableData(
            listToConnect.map((item: IProductOfferingsSubscriberActivationItem) => ({
                id: item.id,
                accountNumber: (
                    <LinkBlock
                        isExternalUrl={isPageAccountInfoUrlExternal(componentsFeature)}
                        url={getPageAccountInfoUrl(componentsFeature, item.account.id.toString())}
                    >
                        {item.account.number}
                    </LinkBlock>
                ),
                balance: item.balance ? `${item.balance} ${translationCommon.currency}` : '',
                creditLimit: item.creditLimit ? `${item.creditLimit} ${translationCommon.currency}` : '',
                msisdn: <LinkBlock url={RoutePath.subscriberId.replace(CLIENT_ID, item.id)}>{item.msisdn}</LinkBlock>,
                cost: (
                    <div style={{ color: isCostWarn(item) ? 'red' : '' }}>
                        {item.cost || 0} {translationCommon.currency}
                    </div>
                ),
                info: getItemInfo(item),
                delete: (
                    <div>
                        {removedSubscribers.includes(item.id) && !itemsWithCustomConflicts.includes(item.id) && (
                            <WizardStepConfirmationRemovedItemStyled style={{ position: 'absolute', left: 0, top: 0 }}>
                                <span>{stepTranslations.removedFromList}</span>
                                <LinkBlock
                                    dataName="wizard-product-offering-table-recover"
                                    onClick={() => recoverHandler(item.id)}
                                >
                                    {stepTranslations.recoveryToList}
                                </LinkBlock>
                            </WizardStepConfirmationRemovedItemStyled>
                        )}
                        {!itemsWithCustomConflicts.includes(item.id) && (
                            <ImgStyled
                                data-name="wizard-product-offering-table-remove"
                                src={icons.removeSmallOpacityIcon}
                                width="24px"
                                onClick={() => removeHandler(item.id)}
                            />
                        )}
                    </div>
                ),
                status: getItemStatus(item),
            })),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        componentsFeature,
        listToConnect,
        removedSubscribers,
        stepTranslations,
        translationCommon,
        itemsWithCustomConflicts,
    ]);

    /**
     * Calc count and totalCost
     */
    useEffect(() => {
        const listFiltered = listToConnect.filter((item) => !removedSubscribers.includes(item.id));

        setSubsCount(listFiltered.length);
        setTotalPrice(
            listFiltered.reduce((acc, cur) => {
                return acc + (cur.cost || 0);
            }, 0),
        );
    }, [listToConnect, removedSubscribers]);

    const tableColumns: ISimpleTableColumns[] = [
        {
            id: EProductSubscribersActivationListField.ACCOUNT_NUMBER,
            title: stepTranslations.subscriberActivationListTitles[
                EProductSubscribersActivationListField.ACCOUNT_NUMBER
            ],
            width: 120,
        },
        {
            id: EProductSubscribersActivationListField.MSISDN,
            title: stepTranslations.subscriberActivationListTitles[EProductSubscribersActivationListField.MSISDN],
            width: 140,
        },
        {
            flex: 2,
            id: EProductSubscribersActivationListField.STATUS,
            title: stepTranslations.subscriberActivationListTitles[EProductSubscribersActivationListField.STATUS],
        },
        {
            hide: isDisconnect,
            id: EProductSubscribersActivationListField.COST,
            title: stepTranslations.subscriberActivationListTitles[EProductSubscribersActivationListField.COST],
            align: 'right',
        },
        {
            id: EProductSubscribersActivationListField.INFO,
            title: stepTranslations.subscriberActivationListTitles[EProductSubscribersActivationListField.INFO],
            align: 'right',
            style: {
                verticalAlign: 'middle',
            },
            width: 40,
        },
        {
            id: EProductSubscribersActivationListField.DELETE,
            title: '',
            align: 'right',
            width: 40,
        },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const delayedHandleNotificationChange = useCallback(
        debounce(() => {
            storageSave();
        }, DEBOUNCE_DEFAULT_DELAY),
        [storageSave],
    );

    return (
        <WizardConfirmationStep
            isReportSubscribers={true}
            startValidate={startValidate}
            storageSave={delayedHandleNotificationChange}
            summaryParams={summaryParams}
            summaryTitle={
                !isDisconnect
                    ? translationProductOfferings.wizardSummary.activationTitle
                    : translationProductOfferings.wizardSummary.deactivationTitle
            }
            tableColumns={tableColumns}
            tableData={tableData}
            wizardReport={translationProductOfferings.wizardReport}
        />
    );
};

interface IConnectedProps {
    componentsFeature: guardComponentType;
    dateFrom: string;
    dateTo: string;
    timeFrom: string;
    timeTo: string;
    isRoamingProductsFeatureEnabled: boolean;
    pageFrom: EProductsPageFrom;
    isDisconnect: boolean;
    listToConnect: IProductOfferingsSubscriberActivationItem[];
    productOfferingsList: IProductOfferingsListItem[];
    megaPowersList: IMegaPowersTotal;
    removedSubscribers: string[];
    translationCommon: ICommonTranslation;
    translationProductOfferings: IWizardProductOfferingsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    dateFrom: makeSelectProductOfferingsDateFrom(),
    dateTo: makeSelectProductOfferingsDateTo(),
    timeFrom: makeSelectProductOfferingsTimeFrom(),
    timeTo: makeSelectProductOfferingsTimeTo(),
    isRoamingProductsFeatureEnabled: makeSelectFeatureEnabled(FeatureNameEnum.ROAMING_PRODUCTS),
    pageFrom: makeSelectProductsPageFrom(),
    isDisconnect: makeSelectProductOfferingsIsDisconnect(),
    listToConnect: makeSelectProductOfferingsListToConnect(),
    productOfferingsList: makeSelectProductOfferingsProductOfferings(),
    megaPowersList: makeSelectProductOfferingsTotalMegaPowers(),
    removedSubscribers: makeSelectProductOfferingsRemovedSubscribers(),
    translationCommon: makeSelectTranslationCommon(),
    translationProductOfferings: makeSelectTranslationWizardProductOfferings(),
});

export interface IDispatchProps {
    getSubscribersCheckList: () => void;
    setParams: (payload: IProductOfferingsSetParamsActionPayload) => void;
    startValidate: () => void;
    storageSave: () => void;
    productStorageSave: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getSubscribersCheckList: () => dispatch(productOfferingsGetSubscribersCheckListAction()),
    setParams: (payload) => dispatch(productOfferingsSetParamsAction(payload)),
    startValidate: () => dispatch(productOfferingsStartValidateAction()),
    storageSave: () => dispatch(wizardReportStorageSaveAction()),
    productStorageSave: () => dispatch(productOfferingsSaveStorageAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductOfferingsStepConfirmation);
